import { useEffect, useState } from "react";
import {
  Megaphone, LayoutPanelTop, Share2, Download, Plus, Trash2, Copy, Eye, EyeOff, Save, Wrench, Truck, ToggleLeft, ListTree, ArrowUp, ArrowDown, QrCode, MessageCircle, MousePointerClick, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtBRL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TABS = [
  ["faixa", "Faixa de Avisos", Megaphone],
  ["popups", "Pop-ups", LayoutPanelTop],
  ["colecoes", "Coleções e Links", Share2],
  ["catalogos", "Catálogos no Rodapé", ListTree],
  ["qrcodes", "QR Codes", QrCode],
  ["cliques", "Cliques WhatsApp", MousePointerClick],
  ["animacoes", "Animações e Efeitos", Sparkles],
  ["entrega", "Entrega e Contador", Truck],
  ["recursos", "Recursos da Loja", ToggleLeft],
  ["assistencia", "Assistência", Wrench],
  ["backup", "Backup", Download],
];

// ---------------- animações e efeitos (tudo CSS/transform, leve) ----------------
const ANIM_ITEMS = [
  ["banner_transitions", "Transições dos banners", "Troca suave e rotação automática do carrossel principal"],
  ["product_hover", "Efeito ao passar o mouse nos produtos", "Realce discreto dos cards nas vitrines"],
  ["gallery_drag", "Arrastar galerias (mouse e dedo)", "Fotos dos produtos e avaliações deslizam ao arrastar"],
  ["menu_transitions", "Transições de menus e categorias", "Abertura elegante do menu e dos submenus"],
  ["button_effects", "Efeitos nos botões", "Resposta tátil nos botões de compra e WhatsApp"],
  ["section_entrance", "Entrada suave das seções", "Seções da página inicial aparecem suavemente ao rolar"],
  ["popup_animation", "Animação dos pop-ups", "Entrada discreta dos pop-ups promocionais"],
];

const AnimationsTab = ({ settings, reload }) => {
  const cfg = settings.animations || {};
  const [form, setForm] = useState({ enabled: cfg.enabled !== false, ...cfg });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { animations: form });
      toast.success("Animações atualizadas!");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 max-w-2xl" data-testid="animations-tab">
      <p className="text-sm text-slate-500">
        Todos os efeitos são feitos em CSS acelerado (sem bibliotecas pesadas) e respeitam a preferência
        de "movimento reduzido" do aparelho do cliente automaticamente.
      </p>
      <div className="flex items-center justify-between gap-3 rounded-lg border-2 border-brand/20 bg-blue-50/40 p-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">Animações do site</p>
          <p className="text-xs text-slate-400">Interruptor geral — desligar aqui desativa TODOS os efeitos</p>
        </div>
        <Switch checked={form.enabled !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="anim-master" />
      </div>
      {ANIM_ITEMS.map(([key, label, desc]) => (
        <div key={key} className={`flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3 ${form.enabled === false ? "opacity-40 pointer-events-none" : ""}`}>
          <div>
            <p className="text-sm font-medium text-slate-800">{label}</p>
            <p className="text-xs text-slate-400">{desc}</p>
          </div>
          <Switch
            checked={form[key] !== false}
            onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
            data-testid={`anim-${key.replaceAll("_", "-")}`}
          />
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="animations-save">
        <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
      </Button>
    </div>
  );
};

// ---------------- relatório de cliques no botão de WhatsApp ----------------
const SOURCE_LABELS = { produto: "Página do produto", card: "Cards das vitrines", busca: "Pesquisa", popup: "Pop-up" };

const WaClicksTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/wa-clicks").then((r) => setData(r.data)).catch(() => setData({ total: 0, by_product: [], by_source: {} }));
  }, []);

  if (!data) return <p className="text-sm text-slate-400">Carregando...</p>;
  return (
    <div className="space-y-5" data-testid="wa-clicks-tab">
      <p className="text-sm text-slate-500 max-w-2xl">
        Cada vez que um cliente toca em "Comprar pelo WhatsApp", o clique é registrado aqui —
        sem nenhum dado pessoal. Use o ranking para saber quais produtos mais geram conversa de venda.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-2xl font-heading font-extrabold text-slate-900" data-testid="wa-clicks-total">{data.total}</p>
          <p className="text-xs text-slate-500">cliques totais</p>
        </div>
        {Object.entries(data.by_source || {}).map(([src, n]) => (
          <div key={src} className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-2xl font-heading font-extrabold text-slate-900" data-testid={`wa-clicks-src-${src}`}>{n}</p>
            <p className="text-xs text-slate-500">{SOURCE_LABELS[src] || src}</p>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <h3 className="font-heading font-semibold text-slate-900 p-4 pb-2">Produtos que mais geram WhatsApp</h3>
        {data.by_product.length === 0 ? (
          <p className="text-sm text-slate-400 p-4 pt-1" data-testid="wa-clicks-empty">
            Nenhum clique registrado ainda — os números começam a aparecer assim que os clientes usarem os botões.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2">#</th>
                <th className="px-4 py-2">Produto</th>
                <th className="px-4 py-2 text-right">Cliques</th>
              </tr>
            </thead>
            <tbody>
              {data.by_product.map((r, i) => (
                <tr key={r.product_id} className="border-b border-slate-50" data-testid={`wa-clicks-row-${i}`}>
                  <td className="px-4 py-2.5 text-slate-400">{i + 1}</td>
                  <td className="px-4 py-2.5 text-slate-800">{r.name}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-brand">{r.clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data.last_click_at && (
        <p className="text-xs text-slate-400">Último clique: {new Date(data.last_click_at).toLocaleString("pt-BR")}</p>
      )}
    </div>
  );
};
const QrRow = ({ label, url }) => {
  const [copied, setCopied] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const qrSrc = `${apiBase}/api/qr.png?u=${encodeURIComponent(url)}&size=6`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };
  return (
    <div className="flex items-center gap-3 border border-slate-100 rounded-lg p-2.5" data-testid={`qr-row-${label.slice(0, 20)}`}>
      <img src={qrSrc} alt={`QR Code de ${label}`} className="w-14 h-14 rounded border border-slate-100" loading="lazy" decoding="async" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate">{label}</p>
        <p className="text-[11px] text-slate-400 truncate">{url}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="icon" title="Copiar link" onClick={copy} data-testid={`qr-copy-${label.slice(0, 12)}`}>
          {copied ? <Copy className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </Button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noreferrer"
          title="Compartilhar no WhatsApp"
          data-testid={`qr-share-${label.slice(0, 12)}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md text-green-600 hover:bg-green-50"
        >
          <MessageCircle className="w-4 h-4" />
        </a>
        <a
          href={`${qrSrc}&download=1`}
          title="Baixar QR Code"
          data-testid={`qr-download-${label.slice(0, 12)}`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-600 hover:bg-slate-100"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const QrTab = ({ settings }) => {
  const domain = (settings.company_domain || "comprenahg.com.br").replace(/^https?:\/\//, "");
  const base = `https://${domain}`;
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const roots = cats.filter((c) => !c.parent_id);
  const filteredProducts = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="qr-tab">
      <p className="text-sm text-slate-500 max-w-2xl">
        QR Codes gerados na hora com os <strong>links permanentes</strong> do site oficial ({domain}).
        O mesmo QR continua funcionando e mostrando preço, foto e estoque atualizados — mesmo depois de impresso.
        Baixe para materiais impressos ou compartilhe pelo WhatsApp.
      </p>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2">
        <h3 className="font-heading font-semibold text-slate-900">Catálogos</h3>
        <QrRow label="Catálogo completo" url={`${base}/catalogo`} />
        {roots.map((c) => (
          <div key={c.id} className="space-y-2">
            <QrRow label={`Catálogo: ${c.name}`} url={`${base}/catalogo?categoria=${c.slug}`} />
            {cats.filter((s) => s.parent_id === c.id).map((s) => (
              <div key={s.id} className="ml-6">
                <QrRow label={`Catálogo: ${c.name} — ${s.name}`} url={`${base}/catalogo?categoria=${s.slug}`} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h3 className="font-heading font-semibold text-slate-900">Produtos</h3>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar produto pelo nome..."
          className="max-w-sm"
          data-testid="qr-product-search"
        />
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {filteredProducts.map((p) => (
            <QrRow key={p.id} label={p.name} url={`${base}/produto/${p.id}`} />
          ))}
          {filteredProducts.length === 0 && (
            <p className="text-sm text-slate-400">Nenhum produto encontrado.</p>
          )}
        </div>
      </div>
    </div>
  );
};
const CatalogLinksTab = ({ settings, reload }) => {
  const [links, setLinks] = useState(settings.catalog_links || []);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    const next = [...links];
    [next[i], next[j]] = [next[j], next[i]];
    setLinks(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { catalog_links: links });
      toast.success("Links de catálogo salvos!");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-2xl" data-testid="catalog-links-tab">
      <p className="text-sm text-slate-500">
        Links da coluna "Catálogos" no rodapé do site. Cada link abre o catálogo digital apenas com os produtos
        daquela categoria/subcategoria — pronto para copiar e mandar no WhatsApp.{" "}
        <strong>Lista vazia = gerada automaticamente</strong> com todas as categorias cadastradas.
      </p>
      {links.map((l, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`catalog-link-row-${i}`}>
          <div className="flex flex-col">
            <Button variant="ghost" size="icon" className="h-6" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`catalog-link-up-${i}`}>
              <ArrowUp className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6" disabled={i === links.length - 1} onClick={() => move(i, 1)} data-testid={`catalog-link-down-${i}`}>
              <ArrowDown className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Input
            value={l.label}
            onChange={(e) => setLinks(links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
            placeholder="Nome exibido (ex.: Guarda-roupas de casal)"
            className="h-9"
            data-testid={`catalog-link-label-${i}`}
          />
          <Select
            value={l.slug || "completo"}
            onValueChange={(v) => setLinks(links.map((x, j) => (j === i ? { ...x, slug: v === "completo" ? "" : v } : x)))}
          >
            <SelectTrigger className="w-56 h-9" data-testid={`catalog-link-cat-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="completo">Catálogo completo (todos)</SelectItem>
              {cats.map((c) => (
                <SelectItem key={c.id} value={c.slug}>
                  {c.parent_id ? "— " : ""}{c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setLinks(links.filter((_, j) => j !== i))} data-testid={`catalog-link-del-${i}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setLinks([...links, { label: "", slug: "" }])} data-testid="catalog-link-add">
          <Plus className="w-4 h-4" /> Adicionar link
        </Button>
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="catalog-links-save">
          <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </div>
  );
};

// ---------------- previsão de entrega + contador regressivo ----------------
const DeliveryTab = ({ settings, reload }) => {
  const cfg = settings.delivery_forecast || {};
  const [form, setForm] = useState({
    enabled: cfg.enabled !== false,
    show_countdown: !!cfg.show_countdown,
    cutoff_hour: cfg.cutoff_hour ?? 0,
    text_tomorrow: cfg.text_tomorrow || "Entrega amanhã",
    text_next: cfg.text_next || "Entrega {data}",
    countdown_label: cfg.countdown_label || "Peça nas próximas",
    countdown_suffix: cfg.countdown_suffix || "para pedidos confirmados até meia-noite, nas regiões atendidas.",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { delivery_forecast: form });
      toast.success("Previsão de entrega salva!");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-xl" data-testid="delivery-tab">
      <p className="text-sm text-slate-500">
        A previsão usa o calendário real de entregas (dias, regiões e capacidade) e o estoque do produto.
        "Entrega amanhã" só aparece quando a próxima data disponível é realmente amanhã.
      </p>
      <div className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
        <div>
          <p className="text-sm font-medium text-slate-800">Previsão de entrega na página do produto</p>
          <p className="text-xs text-slate-400">Desativar remove o aviso de previsão por completo</p>
        </div>
        <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="forecast-enabled" />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
        <div>
          <p className="text-sm font-medium text-slate-800">Contador regressivo (horas, minutos e segundos)</p>
          <p className="text-xs text-slate-400">Estilo Mercado Livre: conta até o horário limite do dia</p>
        </div>
        <Switch checked={form.show_countdown} onCheckedChange={(v) => set("show_countdown", v)} data-testid="countdown-enabled" />
      </div>
      <div>
        <Label>Horário limite para pedidos do dia (0 = meia-noite)</Label>
        <Select value={String(form.cutoff_hour)} onValueChange={(v) => set("cutoff_hour", Number(v))}>
          <SelectTrigger className="mt-1.5" data-testid="cutoff-hour"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Meia-noite (23h59)</SelectItem>
            {[12, 14, 15, 16, 17, 18, 20, 22].map((h) => (
              <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Texto "entrega amanhã"</Label>
          <Input value={form.text_tomorrow} onChange={(e) => set("text_tomorrow", e.target.value)} className="mt-1.5" data-testid="forecast-text-tomorrow" />
        </div>
        <div>
          <Label>Chamada do contador</Label>
          <Input value={form.countdown_label} onChange={(e) => set("countdown_label", e.target.value)} className="mt-1.5" data-testid="forecast-countdown-label" />
        </div>
      </div>
      <div>
        <Label>Texto após o contador</Label>
        <Input value={form.countdown_suffix} onChange={(e) => set("countdown_suffix", e.target.value)} className="mt-1.5" data-testid="forecast-countdown-suffix" />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="forecast-save">
        <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
      </Button>
    </div>
  );
};

// ---------------- interruptores gerais dos recursos comerciais ----------------
const FEATURES = [
  ["reviews", "Avaliações de clientes", "Estrelas, média, comentários na página do produto e formulário de avaliação (com moderação no menu Avaliações)"],
  ["filters", "Filtros das listagens", "Painel de filtros por categoria, cor, preço e disponibilidade nas páginas de produtos"],
];

const FeaturesTab = ({ settings, reload }) => {
  const cfg = settings.features || {};
  const [form, setForm] = useState({ reviews: cfg.reviews !== false, filters: cfg.filters !== false });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { features: form });
      toast.success("Recursos atualizados!");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 max-w-xl" data-testid="features-tab">
      <p className="text-sm text-slate-500">Ative ou desative recursos comerciais da loja sem mexer em código.</p>
      {FEATURES.map(([key, label, desc]) => (
        <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3">
          <div>
            <p className="text-sm font-medium text-slate-800">{label}</p>
            <p className="text-xs text-slate-400">{desc}</p>
          </div>
          <Switch
            checked={!!form[key]}
            onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
            data-testid={`feature-${key}`}
          />
        </div>
      ))}
      <p className="text-xs text-slate-400">
        Destaques e seções da home são controlados no <a href="/admin/home" className="text-brand hover:underline">Editor da Home</a>;
        o contador e a previsão, na aba Entrega e Contador; os pop-ups, na aba Pop-ups.
      </p>
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="features-save">
        <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
      </Button>
    </div>
  );
};

// ---------------- assistência técnica (contatos da página /assistencia) ----------------
const AssistTab = ({ settings, reload }) => {
  const cfg = settings.assistencia || {};
  const [form, setForm] = useState({ whatsapp: "", email: "", email_subject: "", ...cfg });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { assistencia: form });
      toast.success("Contatos de assistência salvos!");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-xl" data-testid="assistencia-tab">
      <p className="text-sm text-slate-500">
        Canais exibidos na página pública de <a href="/assistencia" target="_blank" className="text-brand hover:underline">Assistência Técnica</a>.
        São separados do WhatsApp de vendas.
      </p>
      <div>
        <Label>WhatsApp da assistência (com DDI+DDD, só números)</Label>
        <Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="5521997459912" className="mt-1.5" data-testid="assistencia-whatsapp-input" />
      </div>
      <div>
        <Label>E-mail da assistência</Label>
        <Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="hgmoveis2022@gmail.com" className="mt-1.5" data-testid="assistencia-email-input" />
      </div>
      <div>
        <Label>Assunto do e-mail</Label>
        <Input value={form.email_subject} onChange={(e) => set("email_subject", e.target.value)} placeholder="Assistência Técnica - HG Móveis" className="mt-1.5" data-testid="assistencia-subject-input" />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="assistencia-save">
        <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
      </Button>
    </div>
  );
};

// ---------------- faixa de avisos ----------------
const AnnouncementTab = ({ settings, reload }) => {
  const cfg = settings.announcement || {};
  const [form, setForm] = useState({ ...cfg, messages: cfg.messages || [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { announcement: form });
      toast.success("Faixa de avisos salva!");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="announcement-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Faixa no topo do site com mensagens da loja.</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={!!form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="announcement-enabled" />
          {form.enabled ? "Ativada" : "Desativada"}
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label>Modo</Label>
          <Select value={form.mode || "fixed"} onValueChange={(v) => set("mode", v)}>
            <SelectTrigger className="mt-1.5" data-testid="announcement-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">Texto parado</SelectItem>
              <SelectItem value="rotate">Mensagens alternadas</SelectItem>
              <SelectItem value="slide">Deslizamento suave</SelectItem>
              <SelectItem value="marquee">Texto passando (contínuo)</SelectItem>
              <SelectItem value="pulse">Pulsação suave</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Velocidade (segundos)</Label>
          <Input type="number" min="2" max="30" value={form.speed_seconds || 5} onChange={(e) => set("speed_seconds", Number(e.target.value))} className="mt-1.5" data-testid="announcement-speed" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Fundo</Label>
            <Input type="color" value={form.bg_color || "#07335C"} onChange={(e) => set("bg_color", e.target.value)} className="mt-1.5 h-10 p-1" data-testid="announcement-bg" />
          </div>
          <div>
            <Label>Texto</Label>
            <Input type="color" value={form.text_color || "#ffffff"} onChange={(e) => set("text_color", e.target.value)} className="mt-1.5 h-10 p-1" data-testid="announcement-color" />
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Mensagens</Label>
        {form.messages.map((m, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 border border-slate-100 rounded-lg p-2" data-testid={`announcement-row-${i}`}>
            <Select value={m.icon || "none"} onValueChange={(v) => set("messages", form.messages.map((x, j) => (j === i ? { ...x, icon: v } : x)))}>
              <SelectTrigger className="w-28 h-9" data-testid={`announcement-icon-${i}`}><SelectValue placeholder="Ícone" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem ícone</SelectItem>
                <SelectItem value="truck">Caminhão</SelectItem>
                <SelectItem value="tag">Etiqueta</SelectItem>
                <SelectItem value="star">Estrela</SelectItem>
                <SelectItem value="zap">Raio</SelectItem>
                <SelectItem value="phone">Telefone</SelectItem>
                <SelectItem value="gift">Presente</SelectItem>
                <SelectItem value="sparkles">Brilho</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={m.text}
              onChange={(e) => set("messages", form.messages.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
              placeholder="Ex.: Frete fixo R$ 49,99 nas regiões atendidas"
              className="flex-1 min-w-[200px]"
              data-testid={`announcement-text-${i}`}
            />
            <Input
              value={m.link || ""}
              onChange={(e) => set("messages", form.messages.map((x, j) => (j === i ? { ...x, link: e.target.value } : x)))}
              placeholder="Link (opcional)"
              className="max-w-[160px]"
              data-testid={`announcement-link-${i}`}
            />
            <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" title="Fixar: esta mensagem aparece primeiro">
              <input type="checkbox" checked={!!m.pinned} onChange={(e) => set("messages", form.messages.map((x, j) => (j === i ? { ...x, pinned: e.target.checked } : x)))} data-testid={`announcement-pin-${i}`} />
              Fixar
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer" title="Ativar/desativar esta mensagem">
              <Switch checked={m.active !== false} onCheckedChange={(v) => set("messages", form.messages.map((x, j) => (j === i ? { ...x, active: v } : x)))} data-testid={`announcement-active-${i}`} />
            </label>
            <Button variant="ghost" size="icon" onClick={() => set("messages", form.messages.filter((_, j) => j !== i))} data-testid={`announcement-remove-${i}`}>
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => set("messages", [...form.messages, { text: "", link: "", icon: "none", active: true, pinned: false }])} data-testid="announcement-add">
          <Plus className="w-4 h-4" /> Adicionar mensagem
        </Button>
      </div>
      {/* prévia */}
      <div className="rounded-lg py-2 text-center text-sm font-semibold" style={{ backgroundColor: form.bg_color, color: form.text_color }} data-testid="announcement-preview">
        {form.messages.find((m) => m.text)?.text || "Prévia da faixa de avisos"}
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="announcement-save">
        <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar faixa"}
      </Button>
    </div>
  );
};

// ---------------- pop-ups ----------------
const EMPTY_POPUP = { product_id: "", headline: "Oferta especial", mode: "compra", pages: "all", delay_seconds: 8, start_date: "", end_date: "", active: true };

const PopupsTab = () => {
  const [popups, setPopups] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_POPUP);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/popups").then((r) => setPopups(r.data)).catch(() => setPopups([]));
  useEffect(() => {
    load();
    api.get("/products?limit=200").then((r) => setProducts(r.data.items || [])).catch(() => {});
  }, []);

  const create = async () => {
    try {
      await api.post("/admin/popups", form);
      toast.success("Pop-up criado!");
      setForm(EMPTY_POPUP);
      setAdding(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (p) => {
    const { id, created_at, ...rest } = p;
    await api.put(`/admin/popups/${id}`, { ...rest, active: !p.active });
    load();
  };

  const remove = async (p) => {
    await api.delete(`/admin/popups/${p.id}`);
    toast.success("Pop-up removido.");
    load();
  };

  return (
    <div className="space-y-4" data-testid="popups-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Janelas promocionais com produto real. Aparecem no máximo 2x por visitante.</p>
        <Button size="sm" className="rounded-full" onClick={() => setAdding(true)} data-testid="popup-add-open">
          <Plus className="w-4 h-4" /> Novo pop-up
        </Button>
      </div>
      {popups === null ? (
        <div className="animate-pulse h-24 bg-white rounded-xl" />
      ) : popups.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-gray-500">Nenhum pop-up cadastrado.</div>
      ) : (
        popups.map((p) => {
          const prod = products.find((x) => x.id === p.product_id);
          return (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`popup-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{p.headline} — {prod?.name || p.product_id}</p>
                <p className="text-xs text-slate-500">
                  {p.pages === "all" ? "Todas as páginas" : p.pages} • após {p.delay_seconds}s
                  {p.start_date ? ` • de ${p.start_date}` : ""}{p.end_date ? ` até ${p.end_date}` : ""}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => toggle(p)} title={p.active ? "Desativar" : "Ativar"} data-testid={`popup-toggle-${p.id}`}>
                {p.active ? <Eye className="w-4 h-4 text-brand" /> : <EyeOff className="w-4 h-4 text-slate-300" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`popup-delete-${p.id}`}>
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          );
        })
      )}
      {adding && (
        <div className="bg-white border-2 border-brand/30 rounded-xl p-5 space-y-3" data-testid="popup-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Produto *</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="popup-product"><SelectValue placeholder="Escolha o produto" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtBRL(p.promo_price || p.price)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Chamada</Label>
              <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="mt-1.5" data-testid="popup-headline" />
            </div>
            <div>
              <Label>Botão principal</Label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="mt-1.5" data-testid="popup-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compra">Ver produto + WhatsApp</SelectItem>
                  <SelectItem value="whatsapp">Somente WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Onde exibir</Label>
              <Select value={form.pages} onValueChange={(v) => setForm({ ...form, pages: v })}>
                <SelectTrigger className="mt-1.5" data-testid="popup-pages"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as páginas</SelectItem>
                  <SelectItem value="home">Somente página inicial</SelectItem>
                  <SelectItem value="produtos">Catálogo e categorias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aparecer após (segundos)</Label>
              <Input type="number" min="3" max="120" value={form.delay_seconds} onChange={(e) => setForm({ ...form, delay_seconds: Number(e.target.value) })} className="mt-1.5" data-testid="popup-delay" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Início</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" data-testid="popup-start" />
              </div>
              <div>
                <Label>Fim</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" data-testid="popup-end" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={create} disabled={!form.product_id} className="rounded-full" data-testid="popup-save">Criar pop-up</Button>
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-full">Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- coleções (links compartilháveis) ----------------
const CollectionsTab = () => {
  const [collections, setCollections] = useState(null);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/collections").then((r) => setCollections(r.data)).catch(() => setCollections([]));
  useEffect(() => {
    load();
    api.get("/products?limit=200").then((r) => setProducts(r.data.items || [])).catch(() => {});
  }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/collections", { name, product_ids: selected });
      toast.success("Coleção criada!");
      setName(""); setSelected([]); setAdding(false);
      load();
      navigator.clipboard?.writeText(`${window.location.origin}/colecao/${data.slug}`).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="collections-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-600">Monte seleções de produtos e compartilhe o link pelo WhatsApp.</p>
        <Button size="sm" className="rounded-full" onClick={() => setAdding(true)} data-testid="collection-add-open">
          <Plus className="w-4 h-4" /> Nova coleção
        </Button>
      </div>
      {collections === null ? (
        <div className="animate-pulse h-24 bg-white rounded-xl" />
      ) : collections.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-gray-500">Nenhuma coleção ainda.</div>
      ) : (
        collections.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`collection-row-${c.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{c.name}</p>
              <p className="text-xs text-slate-500">{(c.product_ids || []).length} produto(s) • /colecao/{c.slug}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/colecao/${c.slug}`); toast.success("Link copiado!"); }}
              data-testid={`collection-copy-${c.id}`}
            >
              <Copy className="w-4 h-4" /> Copiar link
            </Button>
            <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/admin/collections/${c.id}`); load(); }} data-testid={`collection-delete-${c.id}`}>
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        ))
      )}
      {adding && (
        <div className="bg-white border-2 border-brand/30 rounded-xl p-5 space-y-3" data-testid="collection-form">
          <div>
            <Label>Nome da coleção *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Sala completa até R$ 3.000" className="mt-1.5" data-testid="collection-name" />
          </div>
          <div>
            <Label>Produtos ({selected.length} selecionados)</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar produto..." className="mt-1.5" data-testid="collection-search" />
            <div className="mt-2 max-h-52 overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
              {filtered.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selected.includes(p.id)}
                    onChange={() => setSelected((prev) => prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id])}
                    className="accent-[#0B5394]"
                    data-testid={`collection-pick-${p.id}`}
                  />
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-xs text-slate-400">{fmtBRL(p.promo_price || p.price)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={create} disabled={!name.trim() || selected.length === 0} className="rounded-full" data-testid="collection-save">Criar coleção</Button>
            <Button variant="outline" onClick={() => setAdding(false)} className="rounded-full">Cancelar</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------- backup ----------------
const BackupTab = () => {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `hgmoveis-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      toast.success("Backup baixado! Guarde em local seguro (ex.: Google Drive).");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="backup-tab">
      <div>
        <h3 className="font-semibold text-gray-900">Backup completo da loja</h3>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">
          Baixa um arquivo JSON com <strong>todos</strong> os produtos, categorias, pedidos, avaliações, páginas,
          coleções, pop-ups, regiões de entrega e configurações. Senhas e chaves secretas nunca são incluídas.
          Recomendação: baixe uma vez por semana e guarde no seu Google Drive.
        </p>
      </div>
      <Button onClick={download} disabled={downloading} className="rounded-full" data-testid="backup-download">
        <Download className="w-4 h-4" /> {downloading ? "Gerando..." : "Baixar backup agora"}
      </Button>
      <div className="bg-slate-50 rounded-lg p-3.5 text-xs text-slate-600 leading-relaxed">
        <strong>Restauração:</strong> em caso de problema, me chame aqui no chat e envie o arquivo — a restauração é feita
        com cuidado e sem afetar pedidos novos. <strong>GitHub:</strong> use o botão "Save to Github" da plataforma para manter
        uma cópia do código num repositório privado seu.
      </div>
    </div>
  );
};

export default function AdminMarketing() {
  const [tab, setTab] = useState("faixa");
  const [settings, setSettings] = useState(null);
  const loadSettings = () => api.get("/admin/settings").then((r) => setSettings(r.data));
  useEffect(() => { loadSettings(); }, []);

  return (
    <div className="p-4 sm:p-8 max-w-4xl" data-testid="admin-marketing">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-6">Marketing e Vitrine</h1>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === id ? "bg-brand text-white" : "bg-white text-gray-600 border border-gray-200"}`}
            data-testid={`marketing-tab-${id}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "faixa" && settings && <AnnouncementTab settings={settings} reload={loadSettings} />}
      {tab === "popups" && <PopupsTab />}
      {tab === "colecoes" && <CollectionsTab />}
      {tab === "catalogos" && settings && <CatalogLinksTab settings={settings} reload={loadSettings} />}
      {tab === "qrcodes" && settings && <QrTab settings={settings} />}
      {tab === "cliques" && <WaClicksTab />}
      {tab === "animacoes" && settings && <AnimationsTab settings={settings} reload={loadSettings} />}
      {tab === "entrega" && settings && <DeliveryTab settings={settings} reload={loadSettings} />}
      {tab === "recursos" && settings && <FeaturesTab settings={settings} reload={loadSettings} />}
      {tab === "assistencia" && settings && <AssistTab settings={settings} reload={loadSettings} />}
      {tab === "backup" && <BackupTab />}
    </div>
  );
}
